import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  changeShoppingItem,
  describePAApiError,
  getSpecialistFeed,
  startShoppingList,
  type PersonalShoppingItem,
  type ShoppingCategory,
} from '../../../lib/api/team.js';
import { useSkillBack } from '../../../lib/skillBack.js';
import { PageShell } from '../../stage/PageShell.js';
import { SpecialistVisualIcon, type SpecialistVisualIconName } from '../SpecialistVisualIcon.js';
import { Skeleton } from '../../ui/Skeleton.js';
import './compras.css';

const KICKER = 'Lista de compras';
const CATEGORIES: Array<{ id: ShoppingCategory; label: string; icon: SpecialistVisualIconName }> = [
  { id: 'hortifruti', label: 'Hortifruti', icon: 'produce' },
  { id: 'padaria', label: 'Padaria', icon: 'bread' },
  { id: 'geladeira', label: 'Geladeira', icon: 'fridge' },
  { id: 'despensa', label: 'Despensa', icon: 'pantry' },
  { id: 'bebidas', label: 'Bebidas', icon: 'drinks' },
  { id: 'limpeza', label: 'Limpeza', icon: 'cleaning' },
  { id: 'cuidados', label: 'Cuidados', icon: 'care' },
  { id: 'outros', label: 'Outros', icon: 'other' },
];
const money = (cents: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);

function CheckMark() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <path
        d="M3.5 8.5l3 3 6-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Campo simples de adicionar: o nome basta; quantidade, preço e detalhe ficam a um toque. */
function AddItem() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unit, setUnit] = useState('un');
  const [estimated, setEstimated] = useState('');
  const [note, setNote] = useState('');
  const [more, setMore] = useState(false);
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      changeShoppingItem({
        type: 'add',
        name,
        quantity: Number(quantity.replace(',', '.')),
        unit,
        estimatedPriceCents: estimated
          ? Math.round(Number(estimated.replace(',', '.')) * 100)
          : undefined,
        note: note || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'lista_compras'] });
      setName('');
      setEstimated('');
      setNote('');
      setError('');
    },
    onError: (err) => setError(describePAApiError(err, 'Não consegui guardar este item agora.')),
  });
  const canAdd = Boolean(name.trim()) && !mutation.isPending;
  function submit(event: FormEvent) {
    event.preventDefault();
    if (canAdd) mutation.mutate();
  }
  return (
    <form className="lc-add" onSubmit={submit}>
      <div className="lc-quick">
        <input
          className="lc-input"
          aria-label="Item que está faltando"
          value={name}
          maxLength={90}
          onChange={(e) => setName(e.target.value)}
          placeholder="O que está faltando?"
        />
        <button type="submit" className="pgs-btn solid" disabled={!canAdd}>
          {mutation.isPending ? 'Guardando…' : 'Adicionar'}
        </button>
      </div>
      {more ? (
        <>
          <div className="lc-pair">
            <label className="lc-field">
              <span>Quantidade</span>
              <input
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </label>
            <label className="lc-field">
              <span>Medida</span>
              <select value={unit} onChange={(e) => setUnit(e.target.value)}>
                <option>un</option>
                <option>kg</option>
                <option>g</option>
                <option>L</option>
                <option>ml</option>
                <option>pacote</option>
                <option>caixa</option>
              </select>
            </label>
          </div>
          <div className="lc-pair">
            <label className="lc-field">
              <span>
                Estimativa R$ <i>opcional</i>
              </span>
              <input
                inputMode="decimal"
                value={estimated}
                onChange={(e) => setEstimated(e.target.value)}
                placeholder="0,00"
              />
            </label>
            <label className="lc-field">
              <span>
                Detalhe <i>opcional</i>
              </span>
              <input
                value={note}
                maxLength={120}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Marca, tamanho…"
              />
            </label>
          </div>
        </>
      ) : (
        <button type="button" className="lc-more" onClick={() => setMore(true)}>
          Quantidade, preço e detalhe
        </button>
      )}
      {error && (
        <p className="lc-error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

function SayIt() {
  return (
    <div className="pgs-note">
      <i>“</i>
      <span>
        <b>É só me dizer “acabou o café”</b>
        <small>Eu coloco na lista por você, no corredor certo.</small>
      </span>
    </div>
  );
}

function Dashboard({ items, onBack }: { items: PersonalShoppingItem[]; onBack: () => void }) {
  const qc = useQueryClient();
  const [market, setMarket] = useState(false);
  const [message, setMessage] = useState('');
  const mutation = useMutation({
    mutationFn: changeShoppingItem,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'lista_compras'] }),
    onError: (err) => setMessage(describePAApiError(err, 'Não consegui mudar a lista agora.')),
  });
  const needed = items.filter((item) => item.status === 'needed');
  const picked = items.filter((item) => item.status === 'picked');
  const total = needed.reduce(
    (sum, item) => sum + (item.estimatedPriceCents ?? 0) * item.quantity,
    0,
  );
  const groups = useMemo(
    () =>
      CATEGORIES.map((cat) => ({
        ...cat,
        items: needed.filter((item) => item.category === cat.id),
      })).filter((group) => group.items.length),
    [needed],
  );
  async function share() {
    const text = `Minha lista de compras:\n${needed.map((item) => `• ${item.quantity} ${item.unit} ${item.name}`).join('\n')}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Lista de compras', text });
      else {
        await navigator.clipboard.writeText(text);
        setMessage('Lista copiada.');
      }
    } catch {
      /* cancelamento do compartilhamento */
    }
  }
  const title = needed.length
    ? `${needed.length} ${needed.length === 1 ? 'item' : 'itens'} pela frente`
    : picked.length
      ? 'Tudo no carrinho'
      : 'Lista vazia';
  const lead = market
    ? 'Modo mercado: toque na caixa quando pegar o item.'
    : needed.length
      ? groups.length === 1
        ? 'Separei por corredor: 1 corredor no seu caminho.'
        : `Separei por corredor: ${groups.length} corredores no seu caminho.`
      : 'Nada faltando por aqui.';
  return (
    <PageShell kicker={KICKER} title={title} lead={lead} onBack={onBack}>
      {total > 0 && (
        <div className="pgs-figure">
          <b>{money(total)}</b>
          <small>estimativa do que falta, como referência nas compras</small>
        </div>
      )}
      <div className="pgs-actions">
        <button
          className={`pgs-btn${market ? ' lc-on' : ''}`}
          aria-pressed={market}
          onClick={() => setMarket(!market)}
        >
          {market ? 'Voltar ao planejamento' : 'Estou no mercado'}
        </button>
        <button className="pgs-btn" onClick={share}>
          Compartilhar
        </button>
      </div>
      {message && (
        <p className="lc-msg" role="status">
          {message}
        </p>
      )}
      {!market && <AddItem />}
      <div className={market ? 'lc-list lc-market' : 'lc-list'}>
        {groups.map((group) => (
          <section className="pgs-section" key={group.id}>
            <h2 className="pgs-h2">
              <span className="lc-h2l">
                <SpecialistVisualIcon name={group.icon} size={22} className="lc-aisle-ic" />
                {group.label}
              </span>
              <small>{group.items.length}</small>
            </h2>
            {group.items.map((item) => (
              <div className="pgs-row" key={item.id}>
                <button
                  className="lc-check"
                  aria-pressed={false}
                  aria-label={`Marcar ${item.name}`}
                  onClick={() => mutation.mutate({ type: 'toggle', id: item.id })}
                >
                  <span>
                    <CheckMark />
                  </span>
                </button>
                <span className="pgs-rowt">
                  <b>{item.name}</b>
                  <small>
                    {item.quantity} {item.unit}
                    {item.note ? ` · ${item.note}` : ''}
                  </small>
                </span>
                {item.estimatedPriceCents !== undefined && (
                  <span className="pgs-val">{money(item.estimatedPriceCents * item.quantity)}</span>
                )}
                <button
                  className="lc-remove"
                  aria-label={`Remover ${item.name}`}
                  onClick={() => mutation.mutate({ type: 'delete', id: item.id })}
                >
                  ×
                </button>
              </div>
            ))}
          </section>
        ))}
        {picked.length > 0 && (
          <section className="pgs-section lc-picked">
            <h2 className="pgs-h2">
              No carrinho
              <small>
                {picked.length} de {items.length}
              </small>
            </h2>
            {picked.map((item) => (
              <div className="pgs-row done" key={item.id}>
                <button
                  className="lc-check"
                  aria-pressed={true}
                  aria-label={`Desmarcar ${item.name}`}
                  onClick={() => mutation.mutate({ type: 'toggle', id: item.id })}
                >
                  <span>
                    <CheckMark />
                  </span>
                </button>
                <span className="pgs-rowt">
                  <b>{item.name}</b>
                  <small>toque na caixa para desfazer</small>
                </span>
              </div>
            ))}
            <div className="pgs-actions">
              <button className="pgs-btn" onClick={() => mutation.mutate({ type: 'clear_picked' })}>
                Encerrar compra e limpar marcados
              </button>
            </div>
          </section>
        )}
      </div>
      {market ? <AddItem /> : <SayIt />}
    </PageShell>
  );
}

export function ComprasView() {
  const qc = useQueryClient();
  const back = useSkillBack();
  const query = useQuery({
    queryKey: ['pa-specialist-feed', 'lista_compras'],
    queryFn: () => getSpecialistFeed('lista_compras'),
    refetchInterval: (q) => (q.state.data?.generating ? 1500 : false),
  });
  const start = useMutation({
    mutationFn: startShoppingList,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'lista_compras'] }),
  });
  const config = query.data?.config as
    { configured?: boolean | undefined; items?: PersonalShoppingItem[] } | undefined;

  if (query.isError)
    return (
      <PageShell kicker={KICKER} title="Não consegui abrir a lista agora" onBack={back}>
        <div className="pgs-actions">
          <button className="pgs-btn solid" onClick={() => query.refetch()}>
            Tentar novamente
          </button>
        </div>
      </PageShell>
    );

  if (config && !config.configured)
    return (
      <PageShell
        kicker={KICKER}
        title="Lembrou, entrou na lista"
        lead="Separo o que falta por corredor, pronto para marcar no mercado."
        onBack={back}
      >
        <section className="pgs-section">
          <div className="pgs-row">
            <span className="pgs-ic">
              <SpecialistVisualIcon name="produce" size={24} />
            </span>
            <span className="pgs-rowt">
              <b>Por corredor</b>
              <small>Hortifruti, padaria, limpeza: cada item no seu lugar.</small>
            </span>
          </div>
          <div className="pgs-row">
            <span className="pgs-ic">
              <SpecialistVisualIcon name="shopping" size={24} />
            </span>
            <span className="pgs-rowt">
              <b>Modo mercado</b>
              <small>Itens grandes para marcar enquanto você anda.</small>
            </span>
          </div>
          <div className="pgs-row">
            <span className="pgs-ic">
              <SpecialistVisualIcon name="groups" size={24} />
            </span>
            <span className="pgs-rowt">
              <b>Para dividir</b>
              <small>Compartilho a lista com quem vai às compras.</small>
            </span>
          </div>
        </section>
        <div className="pgs-actions">
          <button
            className="pgs-btn solid"
            onClick={() => start.mutate()}
            disabled={start.isPending}
          >
            {start.isPending ? 'Abrindo a lista…' : 'Começar minha lista'}
          </button>
        </div>
        <SayIt />
      </PageShell>
    );

  if (config?.configured && !config.items?.length)
    return (
      <PageShell
        kicker={KICKER}
        title="Lista vazia"
        lead="Comece com um item. Depois, é só me dizer quando algo acabar."
        onBack={back}
      >
        <AddItem />
        <SayIt />
      </PageShell>
    );

  if (config?.configured) return <Dashboard items={config.items ?? []} onBack={back} />;

  return (
    <PageShell kicker={KICKER} title="Abrindo sua lista" onBack={back}>
      <Skeleton variant="rows" label="Abrindo a lista de compras" />
    </PageShell>
  );
}
